// connectionSelection.js — pure derivations behind the "Accounts & Pages" settings tab.
//
// Everything here is a plain function over the JSON that /api/facebook/connection_status and
// /api/instagram/connection_status already return. No React, no DOM, no fetch — so the row
// building, the account→pages cascade expansion, the dedup, and every piece of user-facing
// copy can be unit-tested under `node --test` without rendering anything.
//
// Vocabulary (matches the bulk-remove endpoint contract):
//   facebook_account   — the ONE personal Facebook login a company has (has_one). No id in the
//                        payload; the server resolves it from the current company.
//   facebook_page      — a Page authorized through that login. `id` is the Meta page_id.
//   instagram_account  — an IG profile. `id` is the instagram_user_id. Reached either through
//                        the Facebook login (`stand_alone: false`) or by a direct IG login.

export const FACEBOOK_ACCOUNT = 'facebook_account';
export const FACEBOOK_PAGE = 'facebook_page';
export const INSTAGRAM_ACCOUNT = 'instagram_account';

// A stable per-row identity used for checkbox selection. The single Facebook account has no id,
// so its type IS its key.
export const connectionKey = (type, id) => (
  type === FACEBOOK_ACCOUNT ? FACEBOOK_ACCOUNT : `${type}:${id}`
);

const asSet = (keys) => (keys instanceof Set ? keys : new Set(Array.isArray(keys) ? keys : []));

// ── Row building ────────────────────────────────────────────────────────────────────────────

// Section 1. Exactly zero or one row: the company's Facebook login. Instagram never appears here
// — a direct IG login is a profile, not a login that fans out to Pages, so it lives in section 2.
export const buildAccountRows = (facebook) => {
  const fb = facebook || {};
  if (!fb.facebook_account_exists) return [];
  return [{
    key: FACEBOOK_ACCOUNT,
    type: FACEBOOK_ACCOUNT,
    id: null,
    platform: 'facebook',
    name: 'Facebook',
    meta: 'Personal login · connects your Pages',
    // `token_valid` is the truthful signal; fall back to the coarser `connected` when the
    // endpoint omits it (no account → we already returned above).
    connected: Boolean(fb.token_valid == null ? fb.connected : fb.token_valid),
  }];
};

// Section 2. Facebook Pages first, then every Instagram profile. Every row carries an Active
// switch: off means Vesta stops posting there, without disconnecting it.
export const buildPageRows = (facebook, instagram) => {
  const pages = Array.isArray(facebook?.pages) ? facebook.pages : [];
  const accounts = Array.isArray(instagram?.accounts) ? instagram.accounts : [];

  const pageRows = pages.map((p) => ({
    key: connectionKey(FACEBOOK_PAGE, p.id),
    type: FACEBOOK_PAGE,
    id: p.id,
    platform: 'facebook',
    name: p.name || 'Untitled Page',
    meta: 'Facebook Page',
    // Multi-active is real: several Pages can be on at once, so `active` is a plain per-row
    // boolean, never a radio group.
    canToggle: true,
    active: Boolean(p.active),
    expired: false,
    standAlone: false,
  }));

  const instagramRows = accounts.map((a) => ({
    key: connectionKey(INSTAGRAM_ACCOUNT, a.id),
    type: INSTAGRAM_ACCOUNT,
    id: a.id,
    platform: 'instagram',
    name: a.username || 'Instagram account',
    meta: a.stand_alone ? 'Instagram · direct login' : 'Instagram · via Facebook',
    // Older payloads have no `active`; every Instagram account was posted to then, so read it as on.
    canToggle: true,
    active: a.active !== false,
    expired: Boolean(a.token_expired || (!a.token_valid && a.stand_alone)),
    standAlone: Boolean(a.stand_alone),
  }));

  return [...pageRows, ...instagramRows];
};

// ── Cascade ─────────────────────────────────────────────────────────────────────────────────

// Removing the Facebook login takes every Page authorized through it — that is what the server's
// disconnect already does, and Meta ties the Pages to that login regardless. Instagram profiles
// are NOT cascaded (they survive a Facebook disconnect today), so they must not appear in the
// dialog's cascade list.
export const cascadeKeysForAccount = (accountRow, pageRows) => {
  if (!accountRow || accountRow.type !== FACEBOOK_ACCOUNT) return [];
  return (pageRows || []).filter((r) => r.type === FACEBOOK_PAGE).map((r) => r.key);
};

// Turn a raw selection (or a single row's key, for the row-X path) into the exact, deduped list
// of rows that will be destroyed — plus the numbers the amber cascade note needs.
export const expandSelection = (selectedKeys, { accountRows = [], pageRows = [] } = {}) => {
  const selected = asSet(selectedKeys);
  const seen = new Set();
  const items = [];
  const push = (row) => {
    if (!row || seen.has(row.key)) return;
    seen.add(row.key);
    items.push(row);
  };

  let cascadedPageCount = 0;
  let cascadeAccountName = null;

  accountRows.forEach((account) => {
    if (!selected.has(account.key)) return;
    push(account);
    const kids = cascadeKeysForAccount(account, pageRows);
    kids.forEach((key) => push(pageRows.find((r) => r.key === key)));
    if (kids.length > 0) {
      // Count the whole cascade, not just the rows the user had not already ticked — the note
      // describes what leaves with the login, which is all of them either way.
      cascadedPageCount += kids.length;
      cascadeAccountName = account.name;
    }
  });

  pageRows.forEach((row) => {
    if (selected.has(row.key)) push(row);
  });

  return { items, cascadedPageCount, cascadeAccountName };
};

// The request body for DELETE /api/facebook/connections. The account entry carries no id.
export const toRemovalPayload = (items) => (items || []).map((row) => (
  row.type === FACEBOOK_ACCOUNT ? { type: FACEBOOK_ACCOUNT } : { type: row.type, id: row.id }
));

// ── Selection bookkeeping ───────────────────────────────────────────────────────────────────

export const toggleKey = (selectedKeys, key) => {
  const next = new Set(asSet(selectedKeys));
  if (next.has(key)) next.delete(key); else next.add(key);
  return next;
};

// 'none' | 'some' | 'all' — drives both the checkbox indeterminate state and the
// Select all / Deselect all label for ONE section.
export const sectionSelectionState = (selectedKeys, rows) => {
  const keys = (rows || []).map((r) => r.key);
  if (keys.length === 0) return 'none';
  const selected = asSet(selectedKeys);
  const hits = keys.filter((k) => selected.has(k)).length;
  if (hits === 0) return 'none';
  return hits === keys.length ? 'all' : 'some';
};

export const sectionSelectAllLabel = (selectedKeys, rows) => (
  sectionSelectionState(selectedKeys, rows) === 'all' ? 'Deselect all' : 'Select all'
);

// Select all / Deselect all toggles ONLY the rows of the section it belongs to.
export const toggleSection = (selectedKeys, rows) => {
  const next = new Set(asSet(selectedKeys));
  const keys = (rows || []).map((r) => r.key);
  if (sectionSelectionState(next, rows) === 'all') keys.forEach((k) => next.delete(k));
  else keys.forEach((k) => next.add(k));
  return next;
};

// After a successful removal only the rows that actually went are cleared — anything else the
// user had ticked stays ticked.
export const clearRemovedKeys = (selectedKeys, removedItems) => {
  const next = new Set(asSet(selectedKeys));
  (removedItems || []).forEach((row) => next.delete(row.key));
  return next;
};

// ── Copy ────────────────────────────────────────────────────────────────────────────────────
// Wording is deliberately literal about what removal does TODAY: the connection goes and Vesta
// stops posting to it. Nothing cancels already-scheduled posts, so nothing here may claim that.

export const selectionLabel = (n) => `${n} selected`;

export const confirmTitle = (n) => (
  n === 1 ? 'Remove this connection?' : `Remove ${n} connections?`
);

export const confirmBody = (n) => (
  n === 1
    ? 'Vesta will stop posting here. You can reconnect any time.'
    : 'Vesta will stop posting to these. You can reconnect any time.'
);

export const cascadeNote = (n) => (
  `Includes ${n} Page${n === 1 ? '' : 's'} authorized through your Facebook login. `
  + 'Meta ties them to that login, so they go too.'
);

export const removeCtaLabel = (n) => (n === 1 ? 'Remove' : `Remove all ${n}`);

export const removalToast = (n) => `Removed ${n} connection${n === 1 ? '' : 's'}`;

export const REMOVAL_ERROR = "Meta didn't accept the change. Nothing was removed — try again.";
